/**
 * Item Detail, third pass — the one presentational view.
 *
 * Every element, class name and spacing here is the Lab page's markup
 * (/elevation/lab/item-detail-third-pass, mockup of 2026-10-14). Both the Lab
 * (sample model) and the real item page (/prioritization/items/:itemId, real
 * model) render THIS component, so the two cannot drift apart: a change to how
 * the item page looks is a change here, and it shows on both.
 *
 * The view holds no data of its own. Everything it shows comes from `model`;
 * everything it changes goes out through the handlers and editors on the model.
 * A row that carries an `editor` gets a working Edit (or Add) action that opens
 * the editor in place of the value.
 */
import { FieldSourceMarks, ChangeItThere } from '@/components/prioritization/item-view/FieldSourceMark';
import { ConnectorLogo } from '@/components/ui/ConnectorLogo';
import { keptElsewhere, type FieldSource } from '@/lib/itemFieldSources';
import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ObjectRef } from '@/components/ds/ObjectRef';
import type { ObjectKind } from '@/components/ds/objectKinds';
import { DataTable, Field, TabPanel, TextArea, TextInput, ValueMark, type Column } from '@/components/ds';
import { DetailPageFrame, DetailRail } from '@/components/detail-page/DetailPageFrame';
import { DestinationQuestion, EditorButtons, EditorError, InlineChoiceEditor, InlineTextEditor, OwnedSectionHeading } from '@/components/detail-page/detailPageParts';
import type { ValueMarkView } from '@/lib/valueMark';
import { LABEL_MONO, btnPrimary, btnSecondary, panel, panelInset, status, type Tone } from '@/components/mono/primitives';
import { CellBadge } from '@/components/prioritization/item-view/CellBadge';
import { CriterionReadingHint } from '@/components/prioritization/values/CriterionReadingHint';
import { ChoiceList, type ChoiceOption } from '@/components/shared/ChoiceList';
import { AskAiNeedsKey } from '@/components/shared/AskAiNeedsKey';
import type { AssessmentRow, AttentionLine, CycleNode, Destination, LinkRow, SettingSource } from './itemDetailTypes';

// ChoiceList moved to @/components/shared/ChoiceList, and the page parts to
// @/components/detail-page/detailPageParts; kept exported here so this view's
// old import paths still work (Proposal 09 § 4, coupling 1).
export { DestinationQuestion, EditorButtons, EditorError, InlineChoiceEditor, InlineTextEditor, OwnedSectionHeading };
export { ChoiceList, type ChoiceOption };

const RULE = { borderColor: 'var(--rule)' } as const;
const AI_COLOR = 'var(--text-sec)';

// ── Model ────────────────────────────────────────────────────────────────────

/** Opens an editor in place of a value; `close` ends editing. */
export type RowEditor = (close: () => void) => ReactNode;

/** A link-styled action beside a row (Edit, Add, ✦ Suggest…). No handler = drawn only (Lab). */
export interface ActionLink {
  label: string;
  ai?: boolean;
  onClick?: () => void;
}

/** A button drawn by the view. `null` hides it; no handler = drawn only (Lab). */
export interface ViewButton {
  onClick?: () => void;
  disabled?: boolean;
  title?: string;
}

export interface MenuEntry {
  label: string;
  onClick?: () => void;
  danger?: boolean;
}

/** A LinkRow that can also be edited in place or carry actions. */
export interface ViewLinkRow extends Omit<LinkRow, 'text'> {
  id?: string;
  text: ReactNode;
  /** Element id, so a link can bring this row into view. */
  anchorId?: string;
  /** Opens in place of the text. Adds an Edit action (or `editLabel`). */
  editor?: RowEditor;
  editLabel?: string;
  actions?: ActionLink[];
  /** Details shown under the row when it is opened with its triangle. */
  expand?: ReactNode;
}

export interface WhatItIsRow {
  id?: string;
  label: string;
  /** Null shows "Not set." in mustard and the action reads Add. */
  text: ReactNode | null;
  ai?: string | null;
  onAi?: () => void;
  editor?: RowEditor;
  anchorId?: string;
  /** Who decided it (category, altitude): the value mark. */
  mark?: ValueMarkView | null;
}

/** How AI helps with a value, and how the value got there (migration 920). */
export interface ValueAiView {
  /** "AI off" / "AI recommends" / "AI provides". */
  modeLabel: string;
  agentName: string | null;
  on: boolean;
  /** Who decided the value, as an icon (SPEC-value-mark-icon). Null where no AI can judge it. */
  mark?: ValueMarkView | null;
  /** The value's mark: Human, AI recommends · waiting, AI · approved by …, AI · set by rule. */
  statusLabel: string | null;
  statusTone: Tone;
  /** A newer AI recommendation waiting while a value stands. */
  waitingNote: string | null;
  /** For admins: what lies behind the value (the setting, the rule, the agent's reasoning). */
  behind?: ReactNode;
}

/** The AI icon beside a value's name: lit when an agent works on it. */
function ValueAiIcon({ ai }: { ai: ValueAiView }) {
  const label = `${ai.modeLabel}${ai.agentName ? ` — ${ai.agentName}` : ''}`;
  return (
    <span
      className={`ml-1 inline-flex items-center font-mono text-[10.5px] ${ai.on ? '' : 'opacity-50'}`}
      style={{ color: ai.on ? AI_COLOR : 'var(--text-dim)' }}
      title={label}
      aria-label={label}
      data-testid="value-ai-icon"
    >
      ✦
    </span>
  );
}

export interface ViewAssessmentRow extends AssessmentRow {
  /** The attribute the row's value is on, when it is one: its criterion's reading is drawn beside the name. */
  attributeKey?: string;
  ai?: ValueAiView;
  editor?: RowEditor;
  editLabel?: string;
  /** Working controls drawn inside the open row (a criterion's value card: one set of points per scale). */
  body?: ReactNode;
  /** The evidence the Basis column counts ("2 sources"), listed when the row is opened. */
  sources?: ViewLinkRow[];
  /** The linked systems this value comes from or is shared with (its mark beside the name). */
  fieldSources?: FieldSource[];
  /** Set when the value is kept in another system: no edit here, "Change it in <system>" instead. */
  keptThere?: FieldSource;
  /** The agent that works on this value, on the closed row (owner 2026-10-21). */
  agent?: ViewValueAgent;
}

/**
 * The value's agent on the closed Assessment row (owner 2026-10-21): ask it — even when a
 * value stands — and decide its waiting recommendation without opening the row.
 */
export interface ViewValueAgent {
  /** Ask button / can't-run line / the shared no-key line / nothing — from useValueAgents().agentFor. */
  state:
    | { kind: 'none' }
    | { kind: 'cannot_run'; reason: string }
    | { kind: 'needs_key' }
    | { kind: 'ready'; name: string; ask: () => Promise<{ outcome: string; reason?: string }> };
  /** Admins, where no AI works on this value: where to turn it on. */
  turnOnHref?: string | null;
  /** The newest agent recommendation waiting on this value. */
  waiting?: { id: string; agent: string; value: string } | null;
  /** May this person accept or reject that recommendation? (The server checks again.) */
  mayDecide: boolean;
  accept: (proposalId: string) => Promise<void>;
  reject: (proposalId: string, reason: string) => Promise<void>;
}

const stop = (e: { stopPropagation: () => void }) => e.stopPropagation();

/** Under the value's name: ask its agent, say why it can't run, or (admins) where to turn AI on. */
function ValueAgentAsk({ agent, label }: { agent: ViewValueAgent; label: string }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ tone: 'warn' | 'error'; text: string } | null>(null);
  const st = agent.state;
  if (st.kind === 'cannot_run') {
    return <span className="text-[11.5px] font-normal text-[var(--text-dim)]" data-testid="value-agent-cannot-run">{st.reason}</span>;
  }
  if (st.kind === 'needs_key') {
    return (
      <span className="font-normal" onClick={stop}>
        <AskAiNeedsKey />
      </span>
    );
  }
  if (st.kind === 'none') {
    return agent.turnOnHref ? (
      <Link
        to={agent.turnOnHref}
        onClick={stop}
        className="text-[11.5px] font-normal text-[var(--text-dim)] underline decoration-[var(--rule-bright)] underline-offset-[3px] hover:text-[var(--text-pri)]"
        data-testid="value-agent-turn-on"
      >
        Turn on AI for this value
      </Link>
    ) : null;
  }
  if (agent.waiting) return null; // one waiting recommendation at a time; it is decided beside the value
  return (
    <span className="inline-flex flex-col gap-0.5" onClick={stop}>
      <button
        type="button"
        disabled={busy}
        className={`${ACTION_LINK_CLS} text-left hover:text-[var(--text-pri)] disabled:opacity-60`}
        style={{ color: AI_COLOR }}
        title={`Ask ${st.name} to recommend ${label.toLowerCase()}. Nothing changes until a person accepts it.`}
        data-testid="value-agent-ask"
        onClick={async () => {
          setBusy(true);
          setNote(null);
          try {
            const r = await st.ask();
            if (r.outcome === 'abstained') setNote({ tone: 'warn', text: `Could not estimate: ${r.reason ?? 'no reason given'}` });
            else if (r.outcome !== 'proposed') setNote({ tone: r.outcome === 'error' ? 'error' : 'warn', text: r.reason ?? 'Not estimated.' });
          } finally {
            setBusy(false);
          }
        }}
      >
        ✦ {busy ? 'Asking…' : `Ask ${st.name}`}
      </button>
      {note && (
        <span className="text-[11.5px] font-normal normal-case" style={{ color: note.tone === 'error' ? 'var(--coral)' : 'var(--mustard)' }} data-testid="value-agent-note">
          {note.text}
        </span>
      )}
    </span>
  );
}

/** Beside the value: the agent's waiting recommendation, with Accept / Change / Reject. */
function ValueRecommendation({ agent, onChange }: { agent: ViewValueAgent; onChange: () => void }) {
  const w = agent.waiting;
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!w) return null;
  const run = async (act: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await act();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="inline-flex flex-col gap-1" onClick={stop} data-testid="value-recommendation">
      <span className="text-[12px] text-[var(--text-sec)]">
        {w.agent} recommends <strong className="tabular-nums text-[var(--text-pri)]">{w.value}</strong>
      </span>
      {agent.mayDecide && !rejecting && (
        <span className="flex flex-wrap gap-x-2.5 gap-y-1 [&>button]:text-[11px]">
          <button type="button" disabled={busy} className={`${ACTION_LINK_CLS} hover:text-[var(--text-pri)]`} style={{ color: 'var(--text-ter)' }} onClick={() => void run(() => agent.accept(w.id))}>
            Accept
          </button>
          <button type="button" disabled={busy} className={`${ACTION_LINK_CLS} hover:text-[var(--text-pri)]`} style={{ color: 'var(--text-ter)' }} onClick={onChange}>
            Change
          </button>
          <button type="button" disabled={busy} className={`${ACTION_LINK_CLS} hover:text-[var(--text-pri)]`} style={{ color: 'var(--text-ter)' }} onClick={() => setRejecting(true)}>
            Reject
          </button>
        </span>
      )}
      {agent.mayDecide && rejecting && (
        <span className="flex flex-col gap-1">
          <input
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why not? (the agent learns from this)"
            aria-label={`Why reject ${w.agent}'s recommendation`}
            className="w-full border border-[var(--rule)] bg-[var(--surface)] px-2 py-1 text-[12px] text-[var(--text-pri)]"
          />
          <span className="flex gap-3">
            <button
              type="button"
              disabled={busy || !reason.trim()}
              className={`${ACTION_LINK_CLS} hover:text-[var(--text-pri)] disabled:opacity-50`}
              style={{ color: 'var(--text-ter)' }}
              onClick={() => void run(async () => { await agent.reject(w.id, reason); setRejecting(false); setReason(''); })}
            >
              Reject
            </button>
            <button type="button" className={`${ACTION_LINK_CLS} hover:text-[var(--text-pri)]`} style={{ color: 'var(--text-ter)' }} onClick={() => setRejecting(false)}>
              Keep it
            </button>
          </span>
        </span>
      )}
      {error && <span className="text-[11.5px] text-[var(--coral)]">{error}</span>}
    </span>
  );
}

export interface ViewSessionNode {
  name: string;
  href?: string;
  date: string;
  verdict: string;
  tone: Tone;
  settings?: SettingSource[];
  lines: string[];
  flagged?: boolean;
  /** The card the session's name opens in place: its five steps, S1–S5, read for this item. */
  summary?: ReactNode;
}

export interface ViewTrackNode {
  name: string;
  href?: string;
  /** A card of facts about the track, opened from its name (ObjectRef `summary`). */
  summary?: ReactNode;
  fit: string;
  settings: SettingSource[];
  sessions: ViewSessionNode[];
  emptySession?: string;
}

export interface ViewCycleNode extends Omit<CycleNode, 'tracks' | 'outcomes'> {
  href?: string;
  /** What opens over the cycle's name: where the cycle is on its path, its owner, its tracks. */
  summary?: ReactNode;
  tracks: ViewTrackNode[];
  /** `ref`, when present, is drawn marked with its kind in place of `label`. */
  outcomes?: { label: string; text: string; ref?: { kind: ObjectKind; name: string; href?: string | null } }[];
}

export interface LooseEvaluation {
  title: string;
  /** The things named in the title, each marked with its kind; drawn instead of the title when present. */
  refs?: { kind: ObjectKind; name: string; href?: string | null; summary?: ReactNode }[];
  standing: string;
  tone: Tone;
  text: string;
}

export interface TitleEditModel {
  /** The version the item is on now. Null when versions could not be read. */
  version: number | null;
  /** False: no Edit is offered and `cannotEditReason` says why. */
  canEdit: boolean;
  cannotEditReason?: ReactNode;
  /** What the item says now, when the page shows an earlier version (a plan's). An edit starts from it. */
  current?: { title: string; description: string };
  /** After "Saving makes version N+1." — who keeps the earlier version, if anyone. */
  notice: (version: number | null) => string;
  /** Show the "Why it changed" box. Only where the reason is kept. */
  askWhy: boolean;
  /** Resolves with an error message when the save was refused. */
  onSave: (title: string, description: string, why: string) => Promise<string | null | void>;
}

export interface ProposalCardModel {
  label: string;
  note: string;
  from: string;
  to: string;
  why: string;
  decided: boolean;
  decidedText?: string;
  actions: { label: string; onClick?: () => void; disabled?: boolean }[];
  footnote: string;
  error?: string | null;
}

/**
 * Where a link takes you on this page: the destination, then the row to open
 * for editing (`what:why`; `title` opens the title and description), then the
 * element to bring into view.
 */
export interface PageTarget {
  tab: Destination;
  edit?: string;
  anchor?: string;
}

/** One line on the right. It either edits in place (editor) or takes you to where it is kept (to). */
export interface RailRow {
  k: string;
  v: ReactNode;
  editor?: RowEditor;
  to?: PageTarget;
}

/** The item standard (lib/itemStandard.ts), drawn at the top of Assessment. */
export interface ItemStandardView {
  sufficient: boolean;
  /** The working line for "sufficient", in its own words. */
  line: string;
  answers: {
    id: string;
    question: string;
    holds: boolean;
    held: string[];
    missing: string[];
    /** Where the gaps are filled in. Only drawn for someone who may fill them in. */
    links: { label: string; to: PageTarget }[];
  }[];
}

export interface RailGroup {
  h: string;
  rows: RailRow[];
}

export interface ItemDetailViewModel {
  backHref?: string;
  badge: { category: string; altitude: number; tag?: string | null } | null;
  state: { label: string; tone: Tone };
  owner: string | null;
  version: number | null;
  askAi: MenuEntry[] | null;
  discuss: { count: number | null; onClick?: () => void };
  watch: { watching: boolean; onToggle?: () => void };
  more: MenuEntry[];
  title: string;
  description: string;
  titleEdit: TitleEditModel;
  /** Title and description, where they cross to a linked system. None: kept in Priorities.ai only. */
  fieldSources?: { title?: FieldSource[]; description?: FieldSource[] };
  standing: { label: string; value: string; note: string }[];
  attention: AttentionLine[];
  attentionNote: string;
  whatItIs: WhatItIsRow[];
  proposal: ProposalCardModel | null;
  /** The standard this item is held to. Off the first screen: it sits at the top of Assessment. */
  standard?: ItemStandardView;
  assessment: {
    intro: ReactNode;
    rows: ViewAssessmentRow[];
    initialOpen: string | null;
    detailActions: (row: ViewAssessmentRow) => ActionLink[];
    empty?: string;
    /** The value dimensions shown here — what a piece of evidence can be tied to. */
    attributeOptions?: { key: string; label: string }[];
  };
  evidence: { rows: ViewLinkRow[]; add: ViewButton | null; empty?: string };
  /** One row per relationship kind (cluster, package, competing…): what it means, what is linked, and Add. */
  relationships: { rows: ViewLinkRow[] };
  /** Links of a kind not listed in relationships, plus Find (AI). */
  links: { rows: ViewLinkRow[]; add: ViewButton | null; find: ViewButton | null; empty?: string };
  /** The strategy this work supports: strategy → objective → goal. Null when Strategy is off. */
  strategy: { rows: ViewLinkRow[]; empty?: string } | null;
  commitments: { rows: ViewLinkRow[]; empty?: string } | null;
  people: ViewLinkRow[];
  prioritization: { intro: string; cycles: ViewCycleNode[]; loose: LooseEvaluation[]; empty?: string };
  delivery: { title: string; owned?: string; rows: ViewLinkRow[]; empty?: string }[];
  versions: { rows: ViewLinkRow[]; empty?: string };
  actors: { rows: ViewLinkRow[]; empty?: string; extra?: ViewButton | null; extraLabel?: string };
  /** One sentence for "How it was created": who or what made the item, when, and whether a person reviewed it. */
  creation?: string;
  identity: { rows: ViewLinkRow[]; journey: ViewButton | null; empty?: string };
  /** Lineage actions — split, merge, fork, supersede — first on History. Each row says what the act does,
   *  lists what is already recorded, and carries the action when the person may take it. */
  lineage: { rows: ViewLinkRow[] } | null;
  rail: RailGroup[];
}

export interface ItemDetailViewProps {
  model: ItemDetailViewModel;
  /** Controlled destination (the real page keeps it in the URL). */
  tab?: Destination;
  onTabChange?: (tab: Destination) => void;
  /** Extra content inside a destination's framing, after its sections. */
  slots?: Partial<Record<Destination, ReactNode>>;
  /** Drawn first in a destination, under its question (banners, the decision trail). */
  topSlots?: Partial<Record<Destination, ReactNode>>;
  /** Replaces the version rows on History (the versions panel with its ranking flags). */
  versionsPanel?: ReactNode;
  /** Small print under the right-hand column (the hierarchy versions the item started under). */
  railFootnote?: ReactNode;
  /** Under the standing strip: the plan-of-record band when the item is in a plan in force. */
  banner?: ReactNode;
  /** More header actions, before More ▾ (the real page's Ask). */
  extraActions?: ReactNode;
  /** Action: Planning & delivery comes right after Overview. */
  deliveryFirst?: boolean;
  /** Shown right under the "Other items" rows (the add-a-link flow). */
  className?: string;
}

// ── Pieces (the Lab's own) ───────────────────────────────────────────────────

/** Lines that sit level across cells of one row — one per scale an attribute is measured on. */
function ScaleLineStack({ lines }: { lines: ReactNode[] }) {
  return (
    <div className="flex flex-col">
      {lines.map((line, i) => (
        <div key={i} className={`flex min-h-[1.9rem] items-center${i > 0 ? ' border-t border-dashed' : ''}`} style={i > 0 ? { borderColor: 'var(--rule)' } : undefined}>
          {line}
        </div>
      ))}
    </div>
  );
}

function ScopeTag({ children }: { children: ReactNode }) {
  return (
    <span className="whitespace-nowrap border px-1.5 font-mono text-[11px] uppercase tracking-[0.07em] text-[var(--text-ter)]" style={{ borderColor: 'var(--rule-bright)' }}>
      {children}
    </span>
  );
}

const ACTION_LINK_CLS = 'cursor-pointer whitespace-nowrap font-mono text-[12px] uppercase tracking-[0.07em] underline underline-offset-[3px]';

export function ActionLinkView({ action }: { action: ActionLink }) {
  const style = { color: action.ai ? AI_COLOR : 'var(--text-ter)' };
  const content = (
    <>
      {action.ai ? '✦ ' : ''}
      {action.label}
    </>
  );
  if (!action.onClick) {
    return (
      <span className={ACTION_LINK_CLS} style={style}>
        {content}
      </span>
    );
  }
  return (
    <button type="button" className={`${ACTION_LINK_CLS} hover:text-[var(--text-pri)]`} style={style} onClick={action.onClick}>
      {content}
    </button>
  );
}

function EmptyRow({ children, tone = 'dim' }: { children: ReactNode; tone?: 'dim' | 'mustard' }) {
  return (
    <div className="border-b py-2.5" style={RULE}>
      <span style={{ color: tone === 'mustard' ? 'var(--mustard)' : 'var(--text-dim)' }}>{children}</span>
    </div>
  );
}

/** One row being edited at a time, across the whole view. */
interface EditingState {
  editing: string | null;
  setEditing: (key: string | null) => void;
}

function rowKey(r: ViewLinkRow) {
  return r.id ?? `${r.kind}${typeof r.text === 'string' ? r.text : ''}`;
}

/** A disclosure triangle: points right when closed, down when open. */
export function DisclosureTriangle({ open, onClick, label }: { open: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      // A table row may toggle on click too; the triangle handles its own click.
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      aria-expanded={open}
      aria-label={`${open ? 'Hide' : 'Show'} ${label}`}
      className="-ml-1 inline-flex h-6 w-6 shrink-0 items-center justify-center text-[var(--text-ter)] hover:bg-[var(--hover-tint)] hover:text-[var(--text-pri)]"
    >
      <svg viewBox="0 0 10 10" className={`h-2.5 w-2.5 transition-transform ${open ? 'rotate-90' : ''}`} aria-hidden>
        <path d="M2 1 L8 5 L2 9 Z" fill="currentColor" />
      </svg>
    </button>
  );
}

/**
 * Label-and-value rows, each edited in place ("What it is", custom fields).
 * Pass `ed` to share the page's one-editor-open-at-a-time state; without it the
 * list keeps its own.
 */
export function WhatItIsRowList({ rows, ed, scope = 'what' }: { rows: WhatItIsRow[]; ed?: EditingState; scope?: string }) {
  const [ownEditing, setOwnEditing] = useState<string | null>(null);
  const editing = ed ? ed.editing : ownEditing;
  const setEditing = ed ? ed.setEditing : setOwnEditing;
  return (
    <>
      {rows.map((w) => {
        const key = `${scope}:${w.id ?? w.label}`;
        const isEditing = editing === key && !!w.editor;
        return (
          <div key={key} id={w.anchorId} className={`flex flex-wrap items-start gap-x-4 gap-y-1 border-b py-2.5${w.anchorId ? ' scroll-mt-24' : ''}`} style={RULE}>
            <span className="w-[170px] shrink-0 text-sm text-[var(--text-dim)]">{w.label}</span>
            {isEditing && w.editor ? (
              <div className="min-w-0 flex-1">{w.editor(() => setEditing(null))}</div>
            ) : (
              // Label and value share font and size; the value is set apart in full ink and medium weight, the label stays quiet.
              <span className="min-w-0 flex-1 font-medium text-[var(--text-pri)]">
                {w.text && w.mark ? (
                  // The text can be a block (category with frames), so text and mark sit side by side as flex items.
                  <span className="inline-flex flex-wrap items-center gap-x-1.5">
                    {w.text}
                    <ValueMark {...w.mark} />
                  </span>
                ) : (
                  w.text ?? <span className="text-[var(--mustard)]">Not set.</span>
                )}
              </span>
            )}
            {!isEditing && (
              <span className="flex gap-3">
                <ActionLinkView action={{ label: w.text ? 'Edit' : 'Add', onClick: w.editor ? () => setEditing(key) : undefined }} />
                {w.ai && <ActionLinkView action={{ label: w.ai, ai: true, onClick: w.onAi }} />}
              </span>
            )}
          </div>
        );
      })}
    </>
  );
}

function LinkRows({ rows, ed, scope, empty }: { rows: ViewLinkRow[]; ed?: EditingState; scope?: string; empty?: string }) {
  const [openRows, setOpenRows] = useState<Set<string>>(() => new Set());
  if (rows.length === 0 && empty) return <EmptyRow>{empty}</EmptyRow>;
  const toggle = (k: string) =>
    setOpenRows((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  const anyExpandable = rows.some((r) => r.expand);
  return (
    <>
      {rows.map((r) => {
        const key = `${scope ?? ''}:${rowKey(r)}`;
        const isEditing = !!ed && ed.editing === key && !!r.editor;
        const actions: ActionLink[] = [
          ...(r.editor && ed && !isEditing ? [{ label: r.editLabel ?? (r.text == null ? 'Add' : 'Edit'), onClick: () => ed.setEditing(key) }] : []),
          ...(isEditing ? [] : r.actions ?? []),
        ];
        return (
          <div key={key} id={r.anchorId} className="border-b py-2.5" style={RULE}>
          <div className="flex flex-wrap items-start gap-x-4 gap-y-1">
            <span className="flex w-[170px] shrink-0 items-start gap-1 text-sm text-[var(--text-dim)]">
              {r.expand ? (
                <DisclosureTriangle open={openRows.has(key)} onClick={() => toggle(key)} label={`${r.kind} details`} />
              ) : (
                anyExpandable && <span className="inline-block w-5 shrink-0" />
              )}
              <span className="pt-0.5">{r.kind}</span>
            </span>
            {isEditing && r.editor ? (
              <div className="min-w-0 flex-1">{r.editor(() => ed?.setEditing(null))}</div>
            ) : (
              <span className="min-w-0 flex-1 font-medium text-[var(--text-pri)]">
                {r.text ?? <span className="text-[var(--mustard)]">Not set.</span>}{' '}
                {r.meaning && <span className="text-[13.5px] font-normal text-[var(--text-dim)]">{r.meaning}</span>}
              </span>
            )}
            {r.verdict && !isEditing && <span {...status(r.tone ?? 'neutral')}>{r.verdict}</span>}
            {actions.length > 0 && (
              <span className="flex gap-3">
                {actions.map((a) => (
                  <ActionLinkView key={a.label} action={a} />
                ))}
              </span>
            )}
          </div>
          {r.expand && openRows.has(key) && <div className="mt-2 pl-6">{r.expand}</div>}
          </div>
        );
      })}
    </>
  );
}

/** The standard: each question, what the record holds for it, and where a gap is filled in. No grade and no score. */
export function StandardSection({ standard, go }: { standard: ItemStandardView; go: (to: PageTarget) => void }) {
  return (
    <section data-testid="item-standard">
      <OwnedSectionHeading title="The standard" owned="read from the record" />
      <p className="mt-2 font-medium text-[var(--text-pri)]" data-testid="item-standard-verdict">
        {standard.sufficient ? 'On the working line, this item is sufficient.' : 'On the working line, this item is not sufficient yet.'}
      </p>
      <p className="mt-1 max-w-[72ch] text-[13.5px] text-[var(--text-dim)]">
        An item meets a standard so it is “self-evident, and can be compared and ranked fairly with other items or priorities.” Each answer says what the record holds, not how good the words
        are. The workspace’s every-item rules count toward the answers.
      </p>
      <div className="mt-2">
        {standard.answers.map((a) => (
          <div key={a.id} className="border-b py-2.5" style={RULE} data-testid={`item-standard-${a.id}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <span className="min-w-0 flex-1 font-medium text-[var(--text-pri)]">{a.question}</span>
              <span {...status(a.holds ? 'green' : 'mustard')}>{a.holds ? '● Holds' : '○ Not yet'}</span>
            </div>
            {a.held.length > 0 && <p className="mt-1 text-[13.5px] text-[var(--text-sec)]">{a.held.join(' ')}</p>}
            {a.missing.length > 0 && <p className="mt-1 text-[13.5px] text-[var(--mustard)]">{a.missing.join(' ')}</p>}
            {a.links.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
                {a.links.map((l) => (
                  <ActionLinkView key={l.label} action={{ label: l.label, onClick: () => go(l.to) }} />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="mt-1.5 text-[13.5px] text-[var(--text-dim)]">{standard.line}</div>
    </section>
  );
}

function SettingGrid({ settings }: { settings: SettingSource[] }) {
  return (
    <div className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1 text-[13.5px] sm:grid-cols-[minmax(110px,auto)_minmax(0,1fr)_auto]">
      {settings.map((s) => (
        <div key={s.setting} className="contents">
          <span className="text-[var(--text-dim)]">{s.setting}</span>
          <span style={{ color: s.warn ? 'var(--mustard)' : 'var(--text-sec)' }}>{s.value}</span>
          <span className="whitespace-nowrap font-mono text-[11.5px] uppercase tracking-[0.06em] text-[var(--text-ter)]">{s.source}</span>
        </div>
      ))}
    </div>
  );
}

function ViewButtonEl({ b, children, ai }: { b: ViewButton; children: ReactNode; ai?: boolean }) {
  return (
    <button
      {...btnSecondary()}
      style={ai ? { borderColor: 'var(--rule-bright)', color: AI_COLOR } : btnSecondary().style}
      onClick={b.onClick}
      disabled={b.disabled}
      title={b.title}
    >
      {children}
    </button>
  );
}

// ── The view ─────────────────────────────────────────────────────────────────

/** Scrolls to an element once it is on the page: the tab it sits on may still be drawing. Gives up after a second. */
function bringIntoView(id: string, block: ScrollLogicalPosition, triesLeft = 20) {
  const el = document.getElementById(id);
  if (el && el.getClientRects().length > 0) el.scrollIntoView({ behavior: 'smooth', block });
  else if (triesLeft > 0) window.setTimeout(() => bringIntoView(id, block, triesLeft - 1), 50);
}

function useCloseOnOutside(open: boolean, setOpen: (o: boolean) => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open, setOpen]);
  return ref;
}

function Menu({ entries, onPick }: { entries: MenuEntry[]; onPick: () => void }) {
  return (
    <div {...panel('absolute right-0 top-11 z-10 min-w-[220px] py-1.5')} role="menu" style={{ borderColor: 'var(--rule-bright)' }}>
      {entries.map((m) => (
        <button
          key={m.label}
          role="menuitem"
          {...btnSecondary('!h-auto w-full !justify-start border-0 py-2 font-sans text-sm normal-case tracking-normal')}
          style={{ border: 0, color: m.danger ? 'var(--coral-text)' : 'var(--text-sec)' }}
          onClick={() => {
            onPick();
            m.onClick?.();
          }}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

export function ItemDetailThirdPassView({ model: m, tab: controlledTab, onTabChange, slots, topSlots, versionsPanel, railFootnote, banner, extraActions, deliveryFirst = false, className = 'mt-6' }: ItemDetailViewProps) {
  const [ownTab, setOwnTab] = useState<Destination>('overview');
  const tab = controlledTab ?? ownTab;
  const setTab = (t: Destination) => {
    setOwnTab(t);
    onTabChange?.(t);
  };
  const [menu, setMenu] = useState<'more' | 'ai' | null>(null);
  const menuRef = useCloseOnOutside(menu !== null, (o) => {
    if (!o) setMenu(null);
  });
  const [editingTitle, setEditingTitle] = useState(false);
  const [draftTitle, setDraftTitle] = useState(m.title);
  const [draftDescription, setDraftDescription] = useState(m.description);
  const [why, setWhy] = useState('');
  const [titleError, setTitleError] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savingTitle, setSavingTitle] = useState(false);
  const [openRow, setOpenRow] = useState<string | null>(m.assessment.initialOpen);
  const [editing, setEditing] = useState<string | null>(null);
  const ed: EditingState = { editing, setEditing };

  const version = m.titleEdit.version;
  // A field kept in another system that takes no changes back is changed there, not here.
  const titleThere = keptElsewhere(m.fieldSources?.title);
  const descriptionThere = keptElsewhere(m.fieldSources?.description);
  const canEditText = m.titleEdit.canEdit && !(titleThere && descriptionThere);
  const nextVersion = version != null ? version + 1 : null;

  // An edit starts from what the item says now, which a plan may keep out of the version shown.
  const editFrom = m.titleEdit.current ?? { title: m.title, description: m.description };
  const startEdit = () => {
    setDraftTitle(editFrom.title);
    setDraftDescription(editFrom.description);
    setWhy('');
    setTitleError('');
    setSaveError(null);
    setEditingTitle(true);
  };
  /** Takes a link to its place on this page: the rail's rows and the standard's links. */
  const goTo = (to: PageTarget) => {
    setTab(to.tab);
    if (to.edit === 'title') {
      startEdit();
      bringIntoView('item-what-it-says', 'start');
      return;
    }
    if (to.edit) setEditing(to.edit);
    // An editor can be taller than the screen, so it is brought in from its top.
    if (to.anchor) bringIntoView(to.anchor, to.edit ? 'start' : 'center');
  };
  const changed = draftTitle.trim() !== editFrom.title || draftDescription.trim() !== editFrom.description;
  const save = async () => {
    if (!draftTitle.trim()) {
      setTitleError('An item needs a title.');
      return;
    }
    if (changed) {
      setSavingTitle(true);
      setSaveError(null);
      try {
        const err = await m.titleEdit.onSave(draftTitle.trim(), draftDescription.trim(), why.trim());
        if (typeof err === 'string' && err) {
          setSaveError(err);
          return;
        }
      } catch (e) {
        setSaveError(e instanceof Error ? e.message : 'The change could not be saved.');
        return;
      } finally {
        setSavingTitle(false);
      }
    }
    setEditingTitle(false);
  };

  const tabs: { id: Destination; label: string; count?: number }[] = [
    // The count is what needs attention; none means no number (owner, 2026-10-15).
    { id: 'overview', label: 'Overview', count: m.attention.length || undefined },
    ...(deliveryFirst ? [{ id: 'delivery' as Destination, label: 'Planning & delivery' }] : []),
    { id: 'connections', label: 'Connections' },
    { id: 'assessment', label: 'Assessment' },
    { id: 'prioritization', label: 'Prioritization' },
    ...(deliveryFirst ? [] : [{ id: 'delivery' as Destination, label: 'Planning & delivery' }]),
    { id: 'history', label: 'History' },
  ];

  const assessmentColumns: Column<ViewAssessmentRow>[] = [
    // Set shares, so one long Basis note cannot squeeze the scales into a sliver (owner, 2026-10-15);
    // the value column is wider since it also carries the agent's waiting recommendation (2026-10-21).
    {
      key: 'attribute',
      header: 'Attribute',
      width: '15%',
      render: (r) => (
        <span className="inline-flex items-start gap-1 font-semibold text-[var(--text-pri)]">
          {r.details || r.note || r.editor ? (
            <DisclosureTriangle
              open={openRow === r.attribute}
              onClick={() => setOpenRow(openRow === r.attribute ? null : r.attribute)}
              label={`${r.attribute} details`}
            />
          ) : (
            <span className="inline-block w-5 shrink-0" />
          )}
          <span className="inline-flex flex-col gap-1 pt-0.5">
            <span className="inline-flex items-start gap-1">
              <span>{r.attribute}</span>
              {r.attributeKey && <CriterionReadingHint attributeKey={r.attributeKey} variant="mark" className="pt-0.5 font-normal" />}
              {r.ai && <ValueAiIcon ai={r.ai} />}
            </span>
            <FieldSourceMarks sources={r.fieldSources} />
            {/* Keyed by the value, so an old "could not estimate" note goes once the value changes. */}
            {r.agent && <ValueAgentAsk key={`${r.accepted ?? ''}|${r.scaleLines?.map((l) => l.value).join('|') ?? ''}`} agent={r.agent} label={r.attribute} />}
          </span>
        </span>
      ),
    },
    {
      key: 'accepted',
      header: 'Accepted value',
      width: '22%',
      // One line per scale (owner, 2026-10-15): the value chosen on that scale, lined up with
      // the scale beside it, instead of every scale's value run together in one string.
      render: (r) => (
        <span className="inline-flex flex-col gap-0.5">
          {r.scaleLines ? (
            <ScaleLineStack
              lines={r.scaleLines.map((l, i) =>
                l.value != null ? (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="font-semibold tabular-nums text-[var(--text-pri)]">{l.value}</span>
                    {l.source && (
                      <span className="inline-flex" title={l.source.title} data-testid="value-source-logo">
                        <ConnectorLogo type={l.source.connectorType} size={14} title={l.source.title} />
                      </span>
                    )}
                    {/* One mark per value: on the first scale's line (valueStatus reads the latest official value). */}
                    {i === r.scaleLines!.findIndex((x) => x.value != null) && r.ai?.mark && <ValueMark {...r.ai.mark} />}
                  </span>
                ) : (
                  <span className="text-[var(--mustard)]">—</span>
                ),
              )}
            />
          ) : r.accepted ? (
            <span className="inline-flex items-center gap-1.5">
              <span className="font-semibold tabular-nums text-[var(--text-pri)]">{r.accepted}</span>
              {r.ai?.mark && <ValueMark {...r.ai.mark} />}
            </span>
          ) : (
            <span className="text-[var(--mustard)]">—</span>
          )}
          {/* Who decided is the icon (SPEC-value-mark-icon, owner 2026-10-16: no "Human" / "AI" text
              labels); its words are in the icon's tooltip. A recommendation waiting on a value not
              yet set still shows its mark. */}
          {r.ai?.mark && !(r.scaleLines ? r.scaleLines.some((l) => l.value != null) : r.accepted) && <ValueMark {...r.ai.mark} />}
          {r.agent?.waiting && <ValueRecommendation agent={r.agent} onChange={() => setOpenRow(r.attribute)} />}
        </span>
      ),
    },
    {
      key: 'scale',
      header: 'Scale or unit',
      width: '20%',
      render: (r) =>
        r.scaleLines ? <ScaleLineStack lines={r.scaleLines.map((l) => <span className="whitespace-nowrap text-[var(--text-sec)]">{l.scale}</span>)} /> : r.scale,
    },
    { key: 'basis', header: 'Basis', width: '25%', render: (r) => <span {...status(r.basisTone, 'normal-case tracking-normal font-sans text-sm !whitespace-normal')}>{r.basis}</span> },
    { key: 'usedIn', header: 'Used as a criterion in', width: '18%' },
  ];
  const opened = m.assessment.rows.find((r) => r.attribute === openRow);
  const openedKey = opened ? `assessment:${opened.attribute}` : '';

  return (
    <DetailPageFrame<Destination>
      className={className}
      back={{ label: 'Back to items', href: m.backHref }}
      identity={
        <>
          {m.badge ? <CellBadge category={m.badge.category} altitude={m.badge.altitude} tag={m.badge.tag} /> : <span {...status('mustard')}>! Not classified</span>}
          <span {...status(m.state.tone)}>{m.state.label}</span>
          <span className="text-[13.5px] text-[var(--text-dim)]">
            Owner{' '}
            {m.owner ? <strong className="text-[var(--text-pri)]">{m.owner}</strong> : <strong className="text-[var(--mustard)]">not set</strong>}
          </span>
          {m.version != null && (
            <span className="text-[13.5px] text-[var(--text-dim)]" data-testid="idtp-version">
              Version {m.version}
            </span>
          )}
        </>
      }
      actions={
        <div className="contents" ref={menuRef}>
          <button
            {...btnSecondary()}
            style={{ borderColor: 'var(--rule-bright)', color: AI_COLOR }}
            aria-expanded={m.askAi ? menu === 'ai' : undefined}
            aria-haspopup={m.askAi ? 'menu' : undefined}
            disabled={m.askAi !== null && m.askAi.length === 0}
            title={m.askAi !== null && m.askAi.length === 0 ? 'No AI assist is turned on for this workspace' : undefined}
            onClick={() => m.askAi && setMenu((o) => (o === 'ai' ? null : 'ai'))}
          >
            ✦ Ask AI
          </button>
          <button {...btnSecondary()} onClick={m.discuss.onClick}>
            {m.discuss.count ? `Discuss · ${m.discuss.count}` : 'Discuss'}
          </button>
          <button {...btnSecondary()} onClick={m.watch.onToggle} aria-pressed={m.watch.onToggle ? m.watch.watching : undefined}>
            {m.watch.watching ? 'Following' : 'Follow'}
          </button>
          {extraActions}
          <button {...btnSecondary()} aria-expanded={menu === 'more'} aria-haspopup="menu" onClick={() => setMenu((o) => (o === 'more' ? null : 'more'))}>
            More ▾
          </button>
          {menu === 'more' && <Menu entries={m.more} onPick={() => setMenu(null)} />}
          {menu === 'ai' && m.askAi && <Menu entries={m.askAi} onPick={() => setMenu(null)} />}
        </div>
      }
      headline={
      <>
      <div id="item-what-it-says" className="scroll-mt-24" />
      {!editingTitle ? (
        <div>
          <h2 className="mt-4 text-[26px] font-semibold leading-tight" style={{ color: 'var(--text-pri)' }}>
            {m.title}{' '}
            <FieldSourceMarks sources={m.fieldSources?.title} className="mr-2" />
            {canEditText && (
              <button {...btnSecondary('!h-7 align-middle')} onClick={startEdit}>
                Edit
              </button>
            )}
          </h2>
          <p className="mt-2 max-w-[84ch] text-[var(--text-sec)]">
            {m.description || <span className="text-[var(--mustard)]">No description yet.</span>}{' '}
            <FieldSourceMarks sources={m.fieldSources?.description} />
          </p>
          {m.titleEdit.canEdit && titleThere && descriptionThere && (
            <p className="mt-1.5">
              <ChangeItThere source={titleThere} />
            </p>
          )}
          {!m.titleEdit.canEdit && m.titleEdit.cannotEditReason && (
            <p className="mt-1.5 text-[13.5px] text-[var(--text-dim)]">{m.titleEdit.cannotEditReason}</p>
          )}
        </div>
      ) : (
        <div
          {...panel('mt-4 space-y-3 p-4')}
          style={{ borderColor: 'var(--slate)' }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setEditingTitle(false);
          }}
        >
          {titleThere ? (
            <div>
              <div className={LABEL_MONO}>Title</div>
              <div className="mt-1 text-[var(--text-pri)]">{m.title}</div>
              <ChangeItThere source={titleThere} />
            </div>
          ) : (
          <Field label="Title" htmlFor="idtp-title" error={titleError || undefined}>
            <TextInput
              id="idtp-title"
              value={draftTitle}
              invalid={!!titleError}
              onChange={(e) => {
                setDraftTitle(e.target.value);
                setTitleError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void save();
                }
              }}
            />
          </Field>
          )}
          {descriptionThere ? (
            <div>
              <div className={LABEL_MONO}>Description</div>
              <div className="mt-1 text-[var(--text-sec)]">{m.description || 'No description yet.'}</div>
              <ChangeItThere source={descriptionThere} />
            </div>
          ) : (
          <Field label="Description" htmlFor="idtp-desc">
            <TextArea id="idtp-desc" rows={3} value={draftDescription} onChange={(e) => setDraftDescription(e.target.value)} />
          </Field>
          )}
          {changed ? (
            <div className="border px-3 py-2.5 text-sm text-[var(--text-sec)]" style={{ borderColor: 'var(--mustard)', background: 'var(--mustard-dim)' }}>
              <strong className="text-[var(--text-pri)]">{nextVersion != null ? `Saving makes version ${nextVersion}.` : 'Saving makes a new version.'}</strong>{' '}
              {m.titleEdit.notice(version)}
              {m.titleEdit.askWhy && (
                <div className="mt-2">
                  <Field label={`Why it changed (kept with version ${nextVersion ?? ''})`} htmlFor="idtp-why">
                    <TextInput id="idtp-why" value={why} onChange={(e) => setWhy(e.target.value)} placeholder="Fixing wording only / Scope changed because…" />
                  </Field>
                </div>
              )}
            </div>
          ) : (
            <div className="text-[13.5px] text-[var(--text-dim)]">Nothing has changed yet, so saving would not make a new version.</div>
          )}
          <EditorError error={saveError} />
          <div className="flex gap-2">
            <button {...btnPrimary()} onClick={() => void save()} disabled={savingTitle}>
              {savingTitle ? 'Saving…' : changed ? (nextVersion != null ? `Save as version ${nextVersion}` : 'Save') : 'Done'}
            </button>
            <button {...btnSecondary()} onClick={() => setEditingTitle(false)} disabled={savingTitle}>
              Cancel
            </button>
          </div>
        </div>
      )}
      </>
      }
      standing={m.standing}
      banner={banner}
      tabs={tabs}
      activeTab={tab}
      onTabChange={setTab}
      tabsLabel="Item destinations"
      rail={
        <DetailRail
          footnote={railFootnote}
          groups={m.rail.map((g) => ({
            h: g.h,
            rows: g.rows.map((r) => {
              const key = `rail:${g.h}:${r.k}`;
              const open = editing === key && !!r.editor;
              return {
                k: r.k,
                v: r.v,
                onClick: r.editor ? () => setEditing(open ? null : key) : r.to ? () => goTo(r.to!) : undefined,
                open: r.editor ? open : undefined,
                editor: open && r.editor ? r.editor(() => setEditing(null)) : undefined,
              };
            }),
          }))}
        />
      }
    >
          <TabPanel id="overview" active={tab}>
            <DestinationQuestion>What is this, and why should we consider it?</DestinationQuestion>
            {topSlots?.overview}
            <OwnedSectionHeading title="Needs attention" owned="each one says where it applies" />
            {m.attention.length === 0 && <EmptyRow>Nothing needs attention.</EmptyRow>}
            {m.attention.map((a) => (
              <div key={a.text} className="grid grid-cols-1 items-baseline gap-x-4 gap-y-1 border-b py-2.5 sm:grid-cols-[auto_minmax(0,1fr)_auto]" style={RULE}>
                <span {...status(a.tone)}>{a.mark}</span>
                <span className="text-[var(--text-sec)]">{a.text}</span>
                <ScopeTag>{a.scope}</ScopeTag>
                {a.extra && <div className="sm:col-span-3">{a.extra}</div>}
              </div>
            ))}
            <div className="mt-1.5 text-[13.5px] text-[var(--text-dim)]">{m.attentionNote}</div>

            <OwnedSectionHeading title="What it is" owned="owned by this item · edit here" />
            <WhatItIsRowList rows={m.whatItIs} ed={ed} scope="what" />

            {m.proposal && (
              <div className="mt-3 border px-4 py-3" style={{ borderColor: 'var(--rule-bright)', background: 'var(--hover-tint)' }} data-testid="idtp-ai-card">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-mono text-[12px] uppercase tracking-[0.07em]" style={{ color: AI_COLOR }}>
                    {m.proposal.label}
                  </span>
                  <span className="text-[13.5px] text-[var(--text-dim)]">{m.proposal.note}</span>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <span className="text-[var(--text-dim)] line-through">{m.proposal.from}</span>
                  <span className="text-[var(--text-dim)]">→</span>
                  <span className="font-semibold text-[var(--text-pri)]">{m.proposal.to}</span>
                </div>
                <div className="mt-1.5 text-[13.5px] text-[var(--text-dim)]">{m.proposal.why}</div>
                {!m.proposal.decided ? (
                  <>
                    <div className="mt-2.5 flex flex-wrap gap-2">
                      {m.proposal.actions.map((a) => (
                        <button key={a.label} {...btnSecondary()} onClick={a.onClick} disabled={a.disabled}>
                          {a.label}
                        </button>
                      ))}
                    </div>
                    <div className="mt-1.5 text-[13.5px] text-[var(--text-dim)]">{m.proposal.footnote}</div>
                    <EditorError error={m.proposal.error ?? null} />
                  </>
                ) : (
                  <div className="mt-2 text-sm text-[var(--text-sec)]">{m.proposal.decidedText}</div>
                )}
              </div>
            )}
            {slots?.overview}
          </TabPanel>

          <TabPanel id="assessment" active={tab}>
            <DestinationQuestion>What do we know, and how do we judge it?</DestinationQuestion>
            {topSlots?.assessment}
            {m.standard && <StandardSection standard={m.standard} go={goTo} />}
            <OwnedSectionHeading id="item-attributes" title="Attributes and values" owned="values are owned by a value collection · shown here" />
            <div className="my-2 text-[13.5px] text-[var(--text-dim)]">{m.assessment.intro}</div>
            {m.assessment.rows.length === 0 && m.assessment.empty ? (
              <EmptyRow>{m.assessment.empty}</EmptyRow>
            ) : (
              <DataTable
                columns={assessmentColumns}
                rows={m.assessment.rows}
                getRowKey={(r) => r.attribute}
                density="compact"
                onRowClick={(r) => setOpenRow((cur) => (cur === r.attribute ? null : r.attribute))}
                renderExpanded={(r) =>
                  r.attribute === opened?.attribute && (opened.details || opened.note || opened.editor || opened.body || opened.sources) ? (
              <div {...panelInset('my-2 p-4')} data-testid="idtp-attr-detail">
                {!opened.body && <div className={LABEL_MONO}>{opened.attribute}</div>}
                {opened.note && <p className="mt-1.5 text-[var(--text-sec)]">{opened.note}</p>}
                {opened.sources && (
                  <div className="mt-3" data-testid="idtp-attr-sources">
                    <div className={LABEL_MONO}>
                      {opened.sources.length === 1 ? 'The source behind this value' : `The ${opened.sources.length} sources behind this value`}
                    </div>
                    <LinkRows rows={opened.sources} ed={ed} scope={`sources:${opened.attribute}`} />
                  </div>
                )}
                {opened.ai?.behind && <div className="mt-3">{opened.ai.behind}</div>}
                {opened.keptThere && (
                  <div className="mt-3" data-testid="idtp-attr-kept-there">
                    <ChangeItThere source={opened.keptThere} />
                  </div>
                )}
                {opened.body && <div className="mt-3">{opened.body}</div>}
                {opened.details && (
                  <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {opened.details.map((d) => (
                      <div key={d.label}>
                        <div className={LABEL_MONO}>{d.label}</div>
                        <div className="text-sm text-[var(--text-sec)]">{d.text}</div>
                      </div>
                    ))}
                  </div>
                )}
                {editing === openedKey && opened.editor ? (
                  <div className="mt-3">{opened.editor(() => setEditing(null))}</div>
                ) : (
                  <div className="mt-3 flex flex-wrap gap-4">
                    {opened.editor && <ActionLinkView action={{ label: opened.editLabel ?? 'Edit', onClick: () => setEditing(openedKey) }} />}
                    {m.assessment.detailActions(opened).map((a) => (
                      <ActionLinkView key={a.label} action={a} />
                    ))}
                  </div>
                )}
              </div>
                              ) : null
                }
                caption="Attributes, accepted values and where each is used"
                captionHidden
              />
            )}
            <OwnedSectionHeading title="Evidence">
              {m.evidence.add && <ViewButtonEl b={m.evidence.add}>Add evidence</ViewButtonEl>}
            </OwnedSectionHeading>
            <LinkRows rows={m.evidence.rows} ed={ed} scope="evidence" empty={m.evidence.empty} />
            {slots?.assessment}
          </TabPanel>

          <TabPanel id="connections" active={tab}>
            <DestinationQuestion>What does it support, what does it depend on, and what does it belong with?</DestinationQuestion>
            {topSlots?.connections}
            {m.strategy && (
              <>
                <OwnedSectionHeading id="item-strategy" title="Strategy it supports" owned="linked to an objective or a goal under it" />
                <LinkRows rows={m.strategy.rows} ed={ed} scope="strategy" empty={m.strategy.empty} />
              </>
            )}
            <OwnedSectionHeading id="item-relationships" title="Relationships" owned="each links to the other item">
              {m.links.find && (
                <ViewButtonEl b={m.links.find} ai>
                  ✦ Find duplicates and links
                </ViewButtonEl>
              )}
            </OwnedSectionHeading>
            <LinkRows rows={m.relationships.rows} ed={ed} scope="relationships" />
            {m.links.rows.length > 0 && (
              <>
                <OwnedSectionHeading title="Other links" />
                <LinkRows rows={m.links.rows} ed={ed} scope="links" />
              </>
            )}
            {m.commitments && (
              <>
                <OwnedSectionHeading title="Promises it keeps" owned="a commitment is its own record, not this item’s category" />
                <LinkRows rows={m.commitments.rows} ed={ed} scope="commitments" empty={m.commitments.empty} />
              </>
            )}
            <OwnedSectionHeading title="People, by what they do" />
            <LinkRows rows={m.people} ed={ed} scope="people" />
            {slots?.connections}
          </TabPanel>

          <TabPanel id="prioritization" active={tab}>
            <DestinationQuestion>Where is it being considered, and what was decided?</DestinationQuestion>
            {topSlots?.prioritization}
            <div className="text-[13.5px] text-[var(--text-dim)]">{m.prioritization.intro}</div>
            {m.prioritization.cycles.length === 0 && m.prioritization.loose.length === 0 && m.prioritization.empty && (
              <div className="mt-2 text-[13.5px] text-[var(--text-dim)]">{m.prioritization.empty}</div>
            )}
            {m.prioritization.cycles.map((c, ci) => (
              <section key={`${c.name}-${ci}`}>
                <OwnedSectionHeading title={<ObjectRef kind="cycle" name={c.name} href={c.href} summary={c.summary} />}>
                  <span {...status(c.standingTone)}>{c.standing}</span>
                </OwnedSectionHeading>
                <div className="ml-1 mt-2 border-l-2 pl-4" style={{ borderColor: 'var(--rule-bright)' }}>
                  {c.tracks.map((t, ti) => (
                    <div key={`${t.name}-${ti}`} {...panelInset('mt-2 px-3.5 py-3')}>
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <strong className="text-[var(--text-pri)]">
                          <ObjectRef kind="track" name={t.name} href={t.href} summary={t.summary} />
                        </strong>
                        <span className="text-[13.5px] text-[var(--text-dim)]">{t.fit}</span>
                      </div>
                      <SettingGrid settings={t.settings} />
                      <div className="ml-1 mt-2 border-l-2 pl-4" style={{ borderColor: 'var(--rule-bright)' }}>
                        {t.sessions.map((s, si) => (
                          <div key={`${s.name}-${si}`} {...panel('mt-2 px-3.5 py-2.5')}>
                            <div className="flex flex-wrap items-baseline justify-between gap-2">
                              <span>
                                <strong className="text-[var(--text-pri)]">
                                  <ObjectRef kind="session" name={s.name} href={s.href} summary={s.summary} />
                                </strong>{' '}
                                <span className="text-[13.5px] text-[var(--text-dim)]">{s.date}</span>
                              </span>
                              <span {...status(s.tone)}>{s.verdict}</span>
                            </div>
                            {s.settings && <SettingGrid settings={s.settings} />}
                            {s.lines.map((l, li) => (
                              <div key={`${li}-${l}`} className="mt-1 text-[13.5px]" style={{ color: s.flagged ? 'var(--mustard)' : 'var(--text-dim)' }}>
                                {l}
                              </div>
                            ))}
                          </div>
                        ))}
                        {t.emptySession && <div {...panel('mt-2 px-3.5 py-2.5 text-[13.5px] text-[var(--text-dim)]')}>{t.emptySession}</div>}
                      </div>
                    </div>
                  ))}
                  {c.outcomes && (
                    <div {...panelInset('mt-2 space-y-1 px-3.5 py-3')}>
                      {c.outcomes.map((o) => (
                        <div key={o.label} className="flex flex-wrap items-baseline justify-between gap-2">
                          <strong className="text-[var(--text-pri)]">{o.ref ? <ObjectRef kind={o.ref.kind} name={o.ref.name} href={o.ref.href} /> : o.label}</strong>
                          <span className="text-[13.5px] text-[var(--text-dim)]">{o.text}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>
            ))}
            {m.prioritization.loose.map((l) => (
              <Fragment key={l.title}>
                <OwnedSectionHeading
                  title={
                    l.refs ? (
                      <span className="inline-flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        {l.refs.map((r) => (
                          <ObjectRef key={`${r.kind}:${r.name}`} kind={r.kind} name={r.name} href={r.href} summary={r.summary} />
                        ))}
                      </span>
                    ) : (
                      l.title
                    )
                  }
                >
                  <span {...status(l.tone)}>{l.standing}</span>
                </OwnedSectionHeading>
                <div className="mt-2 text-[13.5px] text-[var(--text-dim)]">{l.text}</div>
              </Fragment>
            ))}
            {slots?.prioritization}
          </TabPanel>

          <TabPanel id="delivery" active={tab}>
            <DestinationQuestion>When does it happen, and what actually happened?</DestinationQuestion>
            {topSlots?.delivery}
            {m.delivery.map((d) => (
              <Fragment key={d.title}>
                <OwnedSectionHeading title={d.title} owned={d.owned} />
                <LinkRows rows={d.rows} ed={ed} scope={`delivery-${d.title}`} empty={d.empty} />
              </Fragment>
            ))}
            {slots?.delivery}
          </TabPanel>

          <TabPanel id="history" active={tab}>
            <DestinationQuestion>How did it become what it is today?</DestinationQuestion>
            {topSlots?.history}
            {m.lineage && (
              <>
                <OwnedSectionHeading title="Lineage actions" owned="each keeps a link back to this item" />
                <LinkRows rows={m.lineage.rows} ed={ed} scope="lineage" />
              </>
            )}
            <OwnedSectionHeading title="Versions of what it says" owned="each ranking keeps the version it ranked" />
            {versionsPanel ? <div className="mt-3">{versionsPanel}</div> : <LinkRows rows={m.versions.rows} ed={ed} scope="versions" empty={m.versions.empty} />}
            <OwnedSectionHeading title="Who did what" owned="person, agent, or a person accepting an agent">
              {m.actors.extra && <ViewButtonEl b={m.actors.extra}>{m.actors.extraLabel ?? 'More'}</ViewButtonEl>}
            </OwnedSectionHeading>
            <LinkRows rows={m.actors.rows} ed={ed} scope="actors" empty={m.actors.empty} />
            <OwnedSectionHeading title="Where it came from and how it ended" owned="promoted, split, merged, superseded, retired">
              {m.identity.journey && <ViewButtonEl b={m.identity.journey}>Item journey</ViewButtonEl>}
            </OwnedSectionHeading>
            <LinkRows rows={m.identity.rows} ed={ed} scope="identity" empty={m.identity.empty} />
            {slots?.history}
          </TabPanel>
    </DetailPageFrame>
  );
}

export default ItemDetailThirdPassView;
